/*
 * Presence detection for cost-saving watch-pings.
 *
 * The presence-gated poller (Nexus `pollAllPumps`) treats a pump as ACTIVE —
 * and polls it on the fast every-cycle cadence — whenever a client recently
 * sent a watch-ping (it sets `device_state.watching_until = now() + ttl`, ttl
 * ~30s). The client watch-pings (useVisibleWatch on the Pumps list, and the
 * PumpDetail heartbeat) run on a fixed setInterval for as long as the page is
 * MOUNTED. Without gating, an open-but-unattended tab renews that lease forever,
 * pinning its pumps to the fast cadence indefinitely.
 *
 * Observed 2026-06-03: a pump whose admin Pumps + detail pages were left open
 * overnight logged ~41k telemetry messages / 24h (~24/min, ~2s cadence) instead
 * of idling to its 3600s interval. "Walking away" didn't help because a
 * setInterval keeps firing while the tab is open and foreground.
 *
 * isUserPresent() lets the watch hooks skip pings when no human is plausibly
 * present — the tab is hidden, OR there has been no user interaction for
 * PRESENCE_IDLE_MS. When pings stop, the ~30s server lease lapses and the pump
 * falls back to its idle cadence within a cycle. onPresenceResume() fires when
 * the tab becomes visible again so a hook can ping immediately rather than wait
 * a full interval tick.
 */

export const PRESENCE_IDLE_MS = 5 * 60_000; // no interaction for 5 min ⇒ "away"

const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel',
] as const;
const resumeListeners = new Set<() => void>();

let lastActivityMs = Date.now();
let installed = false;

function bump(): void {
  lastActivityMs = Date.now();
}

function install(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  for (const ev of ACTIVITY_EVENTS) {
    window.addEventListener(ev, bump, { passive: true });
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      bump();
      for (const cb of resumeListeners) cb();
    }
  });
}

/** True when the tab is visible AND the user interacted within PRESENCE_IDLE_MS. */
export function isUserPresent(): boolean {
  install();
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  return Date.now() - lastActivityMs < PRESENCE_IDLE_MS;
}

/** Subscribe to tab-became-visible events. Returns an unsubscribe fn. */
export function onPresenceResume(cb: () => void): () => void {
  install();
  resumeListeners.add(cb);
  return () => {
    resumeListeners.delete(cb);
  };
}
